import React, { useEffect, useCallback, useContext } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import { replace } from 'connected-react-router'
import setSelectedBusinessTravellersAndGuests from 'actions/businessTraveller/setSelectedBusinessTravellerEmployeesAndGuests'
import { saveBusinessTravellerSelectedTravellerState } from 'businessTraveller/storage/businessTravellerStorage'
import { getBusinessTravellerSelectEnabled } from 'businessTraveller/utils/getBusinessTravellerSelectEnabled'
import { getQueryParam, setSearchParamValue } from 'lib/url/searchUrlUtils'
import { queryKeyAdults } from 'constants/url'
import { generateRoomOccupants } from 'lib/offer/occupancyUtils'
import { OfferPageActions, OfferPageDispatchAction } from 'contexts/OfferPage/offerPageStateReducer'
import ModalContext from 'contexts/ModalContext'
import BusinessTravellerSelectModal from 'businessTraveller/components/select-traveller/BusinessTravellerSelectHotelsModal'

interface UseShowBusinessTravellerSelectionModalOptions {
  selectedTravellerEmployeeIds?: Array<string>
  selectedTravellerGuestCount?: number
  currentBusinessAccount?: App.BusinessTraveller.CurrentBusinessAccount
  routerLocationSearch: string
  stateDispatch: (value: OfferPageDispatchAction) => void
}

/**
 * Hook to show business traveller selection modal when no travellers are selected
 * and handle the auto-selection for accounts with selection disabled.
 * Also handles updating the room occupancy in offerPageState and URL when travellers are selected.
 *
 * @param options - Configuration options
 * @param options.selectedTravellerEmployeeIds - Currently selected traveller employee IDs from Redux
 * @param options.selectedguestcount - Currently selected guest count from Redux
 * @param options.currentBusinessAccount - Current business account from Redux
 * @param options.isMobileScreen - Whether the screen is a mobile screen
 * @param options.routerLocationSearch - Current router location search string (from routerLocation.search)
 * @param options.stateDispatch - Dispatch function from offerPageState reducer
 */
export function useShowBusinessTravellerSelectionModal(options: UseShowBusinessTravellerSelectionModalOptions) {
  const {
    selectedTravellerEmployeeIds,
    selectedTravellerGuestCount,
    currentBusinessAccount,
    routerLocationSearch,
    stateDispatch,
  } = options

  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  // Callback to update room occupancy when traveller selection is applied
  const handleTravellerSelectionApplied = useCallback((employeeCount: number, guestCount: number) => {
    const adultsCount = Math.max(1, (employeeCount + guestCount))

    if (getQueryParam(routerLocationSearch, queryKeyAdults) !== adultsCount.toString()) {
      // Update offerPageState.rooms with new occupancy
      stateDispatch({
        type: OfferPageActions.setRooms,
        rooms: [generateRoomOccupants({ adults: adultsCount })]
      })

      // Update URL query parameter
      dispatch(replace({ search: setSearchParamValue(routerLocationSearch, queryKeyAdults, adultsCount) }))
    }
  }, [routerLocationSearch, stateDispatch, dispatch])

  useEffect(() => {
    if (selectedTravellerEmployeeIds?.length === 0 && selectedTravellerGuestCount === 0 && currentBusinessAccount?.status === 'success') {
      const isBusinessTravellerSelectEnabled = getBusinessTravellerSelectEnabled(currentBusinessAccount)
      const employeeId = currentBusinessAccount?.employee?.id
      if (!isBusinessTravellerSelectEnabled) {
        // Auto-select the current employee when selection is disabled
        dispatch(setSelectedBusinessTravellersAndGuests([employeeId], 0))
        saveBusinessTravellerSelectedTravellerState([employeeId], 0)
        handleTravellerSelectionApplied(1, 0)
      } else {
        // Show traveller selection modal (delegate to consumer)
        showModal(
          <BusinessTravellerSelectModal
            modalMode="default"
            modalDismissible={false}
            hotelTravellerSelection
            onHandleSelectTravellerApply={handleTravellerSelectionApplied}
          />
        )
      }
    }
  }, [selectedTravellerEmployeeIds, selectedTravellerGuestCount, currentBusinessAccount, handleTravellerSelectionApplied, dispatch, showModal])
}
